<script lang="ts">
    import * as Tabs from "$lib/components/ui/tabs/index.js";
    import * as Card from "$lib/components/ui/card/index.js";
    import * as RadioGroup from "$lib/components/ui/radio-group/index.js";
    import * as Dialog from "$lib/components/ui/dialog/index.js";
    import Button from "@/lib/components/ui/button/button.svelte";
    import { Separator } from "@/lib/components/ui/separator";
    import Label from "@/lib/components/ui/label/label.svelte";
    import Input from "@/lib/components/ui/input/input.svelte";
    import WizardCard from "./WizardCard.svelte";
    import { Textarea } from "$lib/components/ui/textarea";
    import { getRequest } from "@/Util";
    import Dropzone from "svelte-file-dropzone";
    import route from "@/Util/routes";
    import { slide } from "svelte/transition";
    import { cn } from "@/lib/utils";
    import DefaultAvailabilityPicker from "./DefaultAvailabilityPicker.svelte";
    import { type CalendarDefaults } from "@/Common/constants";
    import { getFunctionalWeight, round, suggestAvailabilities, SUGGESTED_WEEKLY_HOURS } from "@/Util/utils";
    import { Checkbox } from "@/lib/components/ui/checkbox";
    import AvatarCropper from "./AvatarCropper.svelte";
    import { COUNTRIES } from "@/Common/countries";
    import { CyclingProgress, SelectSlider } from "@/Common";
    import FirstTimePlanStep from "./FirstTimePlanStep.svelte";

    export let userId: number | string
    export let userFirstName: string
    export let trial: boolean
    export let trainingMedians
    export let defaultAvailabilities: CalendarDefaults

    //@ts-ignore
    const Xert = window.Xert
    // TODO: Add a type for this
    // For example: { strava: { success: true, loggedIn: true, redirect: "https://localhost/strava/authorize", error: { msg: "Error Message" } } }
    type ProviderNames = "strava" | "garmin-health" | "zwift" | "wahoo"
    interface ProviderData {
        redirect: string,
        loggedIn: boolean,
    }
    type Providers = Record<ProviderNames, ProviderData>
    export let providers: Providers

    // Session Data
    interface WizardSessionData {
        savedAt?: number,
        wizardStage: WizardStage,
        athleteSetupTab: AthleteSetupTab,
        athleteForm: {
            birthYear: number,
            weight: number,
            weightUnit: typeof athleteWeightUnit,
            gender: typeof athleteGender,
        }
    }
    // Scoped to the user: sessionStorage lives as long as the browser tab, so a
    // shared "wizard-data" key let a newly created account resume the previous
    // account's half-finished answers. Older unscoped entries are discarded.
    const sessionKey = `wizard-data:${userId}`
    sessionStorage.removeItem('wizard-data') // discard entries saved before the key was scoped

    // The answers are saved only to carry the user through the trip to a provider's page (Connect) and back, so they come
    // back to the step they left. Used once, and only soon after: opening the wizard any other time (a reload, a later visit,
    // an account that was reset) starts it from the beginning.
    const SESSION_MAX_AGE = 15 * 60 * 1000
    function takeSessionData(): WizardSessionData | null {
        try {
            const saved = sessionStorage.getItem(sessionKey)
            sessionStorage.removeItem(sessionKey)
            const data = saved ? JSON.parse(saved) as WizardSessionData : null
            return data && Date.now() - (data.savedAt ?? 0) <= SESSION_MAX_AGE ? data : null
        } catch {
            return null
        }
    }
    let sessionData = takeSessionData() as WizardSessionData // null when there is none, as it was

    function saveSessionData() {
        let sessionData: WizardSessionData = {
            savedAt: Date.now(),
            wizardStage,
            athleteSetupTab,
            athleteForm: {
                birthYear: athleteBirthYear,
                weight: athleteWeight,
                weightUnit: athleteWeightUnit,
                gender: athleteGender,
            }
        }
        sessionStorage.setItem(sessionKey, JSON.stringify(sessionData))
    }

    // Wizard Stages
    type WizardStage = "starting" | "coach" | "athlete"
    let wizardStage: WizardStage = sessionData?.wizardStage ?? "athlete"
    const wizardStageButtons: {title: string, stage: WizardStage, description: string, icon: string}[] = [
        {title: "I'm an Athlete", stage: "athlete", description: "Set up your Cycling Profile on Xert and configure connections to other services to enable us to analyze your data and start a training program.", icon: "fa-bicycle"},
        {title: "I'm a Coach", stage: "coach", description: "Set up your Coach Community on Xert, allowing you to access and manage the data from the athletes that you coach.", icon: "fa-user"},
    ]

    // Athlete Setup
    type AthleteSetupTab = "info" | "connections" | "discover" | "plan"
    // The plan step needs the processed activities, so a saved session can't resume on it
    let athleteSetupTab: AthleteSetupTab = (sessionData?.athleteSetupTab === "plan" ? "discover" : sessionData?.athleteSetupTab) ?? "info"
    // Year of birth, not age: an age typed in once is wrong a year later.
    let athleteBirthYear: number = sessionData?.athleteForm?.birthYear ?? null
    const currentYear = new Date().getFullYear()
    let athleteWeight: number = sessionData?.athleteForm?.weight ?? null
    let athleteWeightUnit: "lb" | "kg" = sessionData?.athleteForm?.weightUnit ?? "kg"
    let athleteGender: "male" | "female" | "other" = sessionData?.athleteForm?.gender ?? null
    let activityType: "strava" | "garmin" | "upload" | "manual" | undefined

    // melt-ui gives a radio group a tab stop only on its first render: rendered again with nothing chosen, every item gets
    // tabindex -1 and Tab skips the whole group. The chosen item is the tab stop, or every item while none is chosen.
    const radioTabIndex = (groupValue: string | null | undefined, value: string) => !groupValue || groupValue === value ? 0 : -1
    
    let athleteSetupInfo = {
        "info": {
            title: "Profile Information",
            description: `${userFirstName}, tell us about yourself !`,
        },
        "connections": {
            title: "Connections",
            description: "Set up your connections to other systems.",
        },
        "discover" : {
            title: "Analyze Your Riding History",
            description: "Xert can analyze your riding history to discover the unique characteristics of your fitness, performance and your training volume.  It will use these for deeper analytics as well as prepare personalized training programs.",
        },
        "plan": {
            title: "Plan your Training",
            description: "Choose what you want to improve and Forecast AI will build a training plan to get you there.",
        },
    }

    const athleteProvidersDetails: Record<ProviderNames,{logo:string, description:string}> = {
        "strava": {
            logo: "/assets/images/providers/strava_full.svg",
            description: "Enable this connection to allow Xert to retrieve your past activities and send/receive new activities."
        },
        "garmin-health": {
            logo: "/assets/images/providers/garmin_full.svg",
            description: "Enable this connection to allow Xert to retrieve your past activities and receive new activities. Xert can also send structured workouts to your Garmin device."
        },
        "zwift": {
            logo: "/assets/images/providers/zwift_full.svg",
            description: "Enable this connection to allow Xert to receive completed activities from Zwift and send structured workouts to the game that appear under 'For You'."
        },
        "wahoo": {
            logo: "/assets/images/providers/wahoo_full.svg",
            description: "Enable this connection to allow Xert to send structured workouts to your Wahoo Elemnt.  Note that completed activities will need to be processed through Strava at this time so be sure to set up that connection."
        },
    }

    // Optional profile fields (moved here from signup)
    let country = ''
    let newsletterOptIn = false
    let profilePhotoBlob: Blob | null = null


    // Manual Inputs
    let manualInputForm: HTMLFormElement
    let manualWeeklyHours: number = SUGGESTED_WEEKLY_HOURS.default
    // Threshold and Peak Power as typed, kept across tab changes (the form is recreated). Null until edited: the defaults
    // from the rider's weight are used.
    let manualFtp: string | null = null
    let manualPp: string | null = null

    // Without any data to go on, suggest a week of rides from the weekly hours (see suggestAvailabilities), until the
    // user changes the availabilities themselves.
    let suggestedAvailabilitiesJson = ''
    let availabilitiesEdited = false
    // Only the days, their durations and intensities decide whether the user changed the suggestion: the availability
    // popovers rewrite other parts of a day (like its time) when they appear, which is not a change by the user.
    const daysAndDurations = (days: CalendarDefaults) => JSON.stringify(Object.entries(days).map(([dow, d]) => [dow, !!d.availability, d.duration, d.intensity ?? 'advisor']))
    function suggestDefaultAvailabilities() {
        if (availabilitiesEdited) return
        defaultAvailabilities = suggestAvailabilities(manualWeeklyHours, defaultAvailabilities)
        suggestedAvailabilitiesJson = daysAndDurations(defaultAvailabilities)
    }
    $: if (activityType === "manual" && manualWeeklyHours > 0) suggestDefaultAvailabilities()
    $: if (suggestedAvailabilitiesJson && daysAndDurations(defaultAvailabilities) !== suggestedAvailabilitiesJson) availabilitiesEdited = true

    // Validation and Button Control
    function athleteSetupBackButton() {
        if (athleteSetupTab === "info") {
            wizardStage = "starting"
        } else if (athleteSetupTab === "connections") {
            athleteSetupTab = "info"
        } else if (athleteSetupTab === "discover") {
            athleteSetupTab = "connections"
        } else if (athleteSetupTab === "plan") {
            athleteSetupTab = "discover"
        }
    }

    /* After Finish: process the user's activities, then continue to the plan step */
    type ProcessingState = "idle" | "garmin" | "running" | "done"
    let processing: ProcessingState = "idle"
    let redirectUrl = "/my-fitness"
    let forecastData = null
    const PROCESSING_EVENT = "wizard_processing_complete"
    const PROCESSING_TIMEOUT = 10 * 60 * 1000

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

    // Resolves true when the server reports the activities are processed, false if that takes too long.
    // The push event is the primary signal; polling covers a missed event. generation: the submission's, as the server
    // gave it; an event for an earlier submission (the wizard submitted again) doesn't count.
    function waitForProcessing(generation: string | null): Promise<boolean> {
        return new Promise(resolve => {
            const channel = Xert._userChannel
            let poll: ReturnType<typeof setInterval>
            let timeout: ReturnType<typeof setTimeout>
            const finish = (ready: boolean) => {
                clearInterval(poll)
                clearTimeout(timeout)
                channel?.unbind(PROCESSING_EVENT, onEvent)
                resolve(ready)
            }
            const onEvent = (data: any) => {
                if (generation && data?.generation && data.generation !== generation) return
                finish(true)
            }
            channel?.bind(PROCESSING_EVENT, onEvent)
            poll = setInterval(async () => {
                try {
                    const status = await window.fetch('wizard/status', getRequest()).then(r => r.json())
                    if (status?.ready) finish(true)
                } catch {}
            }, 5000)
            timeout = setTimeout(() => finish(false), PROCESSING_TIMEOUT)
        })
    }

    // Why Garmin can't be chosen, once it has failed: 'historical_data' (Garmin refused because the user has not switched
    // Historical Data on, which Garmin leaves off and only the user can turn on), 'denied' (refused for another reason) or
    // 'timeout' (no activities came). The Discover step is shown again with the Garmin option greyed out.
    type GarminUnavailable = "historical_data" | "denied" | "timeout"
    let garminUnavailable: GarminUnavailable | null = null
    let showGarminHistoricalDialog = false
    const garminUnavailableMessages: Record<GarminUnavailable, string> = {
        historical_data: "Garmin didn't send your activities because Historical Data isn't switched on for Xert. Garmin leaves it off, and only you can turn it on.",
        denied: "Garmin turned down the request for your activities.",
        timeout: "Garmin didn't send any activities in time. There may be none in the last 90 days, or Historical Data may not be switched on for Xert.",
    }
    // Garmin could not take the request right now (its rate limit, an error of its own): nothing is wrong with the account,
    // so Garmin stays available, and the user tries again.
    let garminBusy = false
    function garminFailed(reason: GarminUnavailable | "busy") {
        processing = "idle"
        disableFinish = false
        athleteSetupTab = "discover"
        garminBusy = reason === "busy"
        if (reason === "busy") return
        garminUnavailable = reason
        activityType = null as any // null, not undefined: the radio group ignores undefined and keeps Garmin chosen
    }

    // Waiting for Garmin to send the activities the wizard requested. The time left is how long Garmin took for recent
    // requests (expected_seconds), counted from when this one was made.
    const GARMIN_RECEIVED_EVENT = "garmin_backfill_received"
    // Garmin can take hours. The user carries on in the app after this long, where the activities are analyzed when they arrive.
    const GARMIN_TIMEOUT_MIN = 10 * 60 * 1000
    let garminWait: {startedAt: number, expectedMs: number} | null = null
    let garminNow = Date.now()
    $: garminElapsed = garminWait ? garminNow - garminWait.startedAt : 0
    $: garminLate = garminWait !== null && garminElapsed > garminWait.expectedMs
    // Up to most of the bar by the time expected, so it doesn't sit near the end while Garmin is still on time
    $: garminPercent = garminWait ? Math.min(85, 85 * garminElapsed / garminWait.expectedMs) : 0
    $: garminTimeLeft = garminWait && !garminLate ? garminWait.expectedMs - garminElapsed : null

    // Resolves true when Garmin has sent the activities, false if it takes too long. The push event is the primary signal;
    // polling covers a missed event.
    function waitForGarmin(expectedSeconds: number, generation: string | null): Promise<boolean> {
        garminWait = {startedAt: Date.now(), expectedMs: expectedSeconds * 1000}
        garminNow = Date.now()
        return new Promise(resolve => {
            const channel = Xert._userChannel
            const clock = setInterval(() => garminNow = Date.now(), 1000)
            let poll: ReturnType<typeof setInterval>
            let timeout: ReturnType<typeof setTimeout>
            const finish = (received: boolean) => {
                clearInterval(clock)
                clearInterval(poll)
                clearTimeout(timeout)
                channel?.unbind(GARMIN_RECEIVED_EVENT, onEvent)
                garminWait = null
                resolve(received)
            }
            const onEvent = (data: any) => {
                if (generation && data?.generation && data.generation !== generation) return
                finish(true)
            }
            channel?.bind(GARMIN_RECEIVED_EVENT, onEvent)
            poll = setInterval(async () => {
                try {
                    const status = await window.fetch('wizard/status', getRequest()).then(r => r.json())
                    if (status && !status.garmin_pending) finish(true)
                } catch {}
            }, 5000)
            timeout = setTimeout(() => finish(false), Math.max(GARMIN_TIMEOUT_MIN, 4 * expectedSeconds * 1000))
        })
    }

    // The wizard is finished once the server records it: until then, reloading the page starts it over.
    let leaving = false
    async function leaveWizard() {
        if (leaving) return
        leaving = true
        for (let attempt = 0; attempt < 3; attempt++) {
            const response = await window.fetch('wizard/complete', { ...getRequest(), method: 'POST' }).catch(() => null)
            if (response?.ok) {
                sessionStorage.removeItem(sessionKey)
                window.location.href = redirectUrl
                return
            }
            await delay(1000)
        }
        leaving = false
        processing = "idle"
        disableFinish = false
        Xert.danger("We could not finish setting up your account. Please try again.")
    }

    async function processActivities(hasActivitiesToProcess: boolean, generation: string | null = null, garminExpectedSeconds: number | null = null) {
        if (garminExpectedSeconds !== null) {
            processing = "garmin"
            if (!await waitForGarmin(garminExpectedSeconds, generation)) return garminFailed("timeout")
        }
        processing = "running"
        // Always long enough for the animation to be seen, even when there is nothing to process.
        // Only wait for the queue when it has activities, otherwise (manual setup, nothing selected) there is nothing
        // for it to finish and the user's fitness is already set up.
        const [ready] = await Promise.all([hasActivitiesToProcess ? waitForProcessing(generation) : true, delay(3500)])
        if (!ready) return leaveWizard() // Still going: carry on in the app, where the training updates as it finishes

        processing = "done"
        const [data] = await Promise.all([
            window.fetch('wizard/forecast-data', getRequest()).then(r => r.json()).catch(() => null),
            delay(2200), // Time to enjoy the check mark
        ])

        // Without a fitness signature (e.g. Garmin history that is still arriving) there is nothing to forecast from
        if (!data?.training_advice?.signature?.ftp) return leaveWizard()
        forecastData = data
        athleteSetupTab = "plan"
        processing = "idle"
        disableFinish = false // Previous goes back to Discover
    }

    let profileInfoForm: HTMLFormElement
    let profileInfoValid: boolean = true
    let analyzeOptionSelected: boolean = true
    let manualFormFilled: boolean = true

    // Newsletter opt-out confirmation — shown once if the user leaves the box unchecked
    let showNewsletterWarning = false
    let newsletterWarningShown = false

    function advanceFromInfo() {
        athleteSetupTab = "connections"
        profileInfoValid = true
    }

    function athleteSetupForwardButton() {
        if (athleteSetupTab === "info") {
            if(profileInfoForm.checkValidity()) {
                if (!newsletterOptIn && !newsletterWarningShown) {
                    showNewsletterWarning = true
                    return
                }
                advanceFromInfo()
            } else profileInfoValid = false

        } else if (athleteSetupTab === "connections") {
            athleteSetupTab = "discover"

        } else if (athleteSetupTab === "discover") {
            if(activityType) {
                analyzeOptionSelected = true

                if (activityType === "manual" && !manualInputForm.checkValidity()) {
                    manualFormFilled = false
                    return 
                } else manualFormFilled = true
                    
                postWizard()
                
            } else analyzeOptionSelected = false      
        }
    }

    // Files
    let files = []
    function handleFileSelect(e) {
        const { acceptedFiles, fileRejections } = e.detail;

        acceptedFiles.forEach((file: File) => {
            if(file.name.split('.').pop() === 'tcx') {
                files = [
                    ...files,
                    ...(file.size <= 31457280 ? [file] : [])
                ]
            } else if (file.name.split('.').pop() === 'fit') {
                files = [
                    ...files,
                    ...(file.size <= 5145728 ? [file] : [])
                ]
            }
        })
    }

    let disableFinish = false
    let photoError = ''
    // The answers the account was set up with. Going Previous from the plan step and then Next again must not set the
    // account up a second time, unless the answers were changed.
    let wizardSubmitted = false
    let submittedAnswers = ''
    function currentAnswers() {
        return JSON.stringify({
            athleteBirthYear, athleteGender, athleteWeight, athleteWeightUnit, activityType, country, newsletterOptIn,
            photo: profilePhotoBlob ? profilePhotoBlob.size : 0,
            files: files.map((f: File) => `${f.name}:${f.size}`),
            manual: activityType === "manual" && manualInputForm ? Object.fromEntries(new FormData(manualInputForm).entries()) : null,
            availabilities: activityType === "manual" ? defaultAvailabilities : null,
        })
    }

    async function postWizard() {
      disableFinish = true
      garminBusy = false

        const answers = currentAnswers()
        if (wizardSubmitted && answers === submittedAnswers && forecastData) {
            // Nothing changed since the account was set up: carry on to the plan step.
            athleteSetupTab = "plan"
            disableFinish = false
            return
        }
        // The answers changed after the account was set up: the server clears that setup when it is submitted again.
        forecastData = null

        // Upload activity files
        for (const file of files) {
            let request_init = getRequest()
            delete request_init.headers['Content-Type']

            let formData = new FormData()
            formData.append('files[]', file)

            await window.fetch(route('ActivityController@postActivityUploadDelay'), {
                    ...request_init,
                    method: "POST",
                    body: formData
                }
            )
        }

        // Upload cropped avatar blob. The photo is optional, so a failure here is
        // reported but must not block the rest of the wizard from being saved.
        photoError = ''
        if (profilePhotoBlob) {
            const photoRequest = getRequest()
            delete photoRequest.headers['Content-Type']
            const photoForm = new FormData()
            photoForm.append('profile_photo', profilePhotoBlob, 'avatar.jpg')
            try {
                const photoRes = await window.fetch('wizard/photo', { ...photoRequest, method: 'POST', body: photoForm })
                if (!photoRes.ok) {
                    const photoData = await photoRes.json().catch(() => ({}))
                    photoError = photoData?.error ?? 'We could not save your profile photo. You can add one later from Account Settings.'
                }
            } catch {
                photoError = 'We could not save your profile photo. You can add one later from Account Settings.'
            }
        }

        const postAvailabilities = {}
        for (const [dow, availability] of Object.entries(defaultAvailabilities)) {
            postAvailabilities[dow] = {
                ...availability,
                time: availability.time === "advisor" ? "advisor" : `${availability.time}:00`
            }
        }

        let timezone = ''
        try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '' } catch {}

        let body = {
            birth_year: athleteBirthYear,
            sex: athleteGender,
            weight: athleteWeight,
            weight_units: athleteWeightUnit,
            activity_type: activityType,
            country: country || undefined,
            email_news_letter: newsletterOptIn,
            // Social signups capture the timezone before the OAuth redirect, but
            // that can be lost (missing tz parameter, dropped session). The wizard
            // is the reliable place to correct it.
            timezone: timezone || undefined,
            manual_inputs: activityType !== "manual" ? undefined : {
                ...Object.fromEntries((new FormData(manualInputForm)).entries()),
                default_availabilities: postAvailabilities
            }
        }

        // A failure the server couldn't report as {error, message} (an unreadable response, or none) gets the general message
        const response = await window.fetch('wizard', {
            ...getRequest(),
            method: 'POST',
            body: JSON.stringify(body)
        }).then(r=>r.json()).catch(() => null);

        if (response?.redirect) {
            redirectUrl = response.redirect
            wizardSubmitted = true
            submittedAnswers = answers
            if (response.garmin_unavailable) return garminFailed(response.garmin_unavailable)
            processActivities(response.processing !== false, response.processing_generation ?? null, response.garmin_backfill?.expected_seconds ?? null)
        } else {
            // postWizardForUser() reports failures as {error, message}: the reason when the user can fix it (e.g. an
            // out-of-range year of birth), otherwise a general message.
            Xert.danger(response?.error && response.message ? response.message
                : "Something went wrong setting up your account. Please try again, and if it keeps happening, email support@xertonline.com.")
            disableFinish = false
        }
    }

</script>

<div class="relative twp min-h-[100dvh] pt-5 sm:pb-10 sm:px-5">
    <img class="absolute top-0 left-0 ml-5 mt-5 h-12 self-start" src="https://alpha.xertonline.com/assets/images/xert_teal_and_gray.png" alt="Xert Logo"/>
    <button
        type="button"
        class="absolute top-0 right-0 mt-5 mr-5 flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
        on:click={async () => {
            await window.fetch('/auth/logout', { ...getRequest(), method: 'POST' })
            window.location.href = '/auth'
        }}
    >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
        </svg>
        Log out
    </button>
    <div class="max-w-6xl mx-auto mt-20">
        {#if wizardStage === "starting"}
            <WizardCard>
                <svelte:fragment slot="content">
                    <Card.Header class="text-center lg:mt-12">
                        <Card.Title class="text-4xl leading-none"> Welcome to XERT! </Card.Title>
                        <Card.Description class="text-lg">Let's get started</Card.Description>
                    </Card.Header>
                    <Card.Content class="mt-4">
                        <div class="flex flex-col gap-8 items-center">
                            {#each wizardStageButtons as button}
                                <button class="flex flex-col text-center text-xerteal-600 justify-center items-center bg-xerteal-50 border border-xerteal-400 max-w-[600px] rounded-md px-10 py-5 select-button transition-all shadow-sm gap-6" on:click={() => wizardStage = button.stage}>
                                    <span class="text-2xl font-semibold">{button.title}</span>
                                    <i class={cn("fa-regular text-xerteal-400 text-4xl ", button.icon)}/>
                                    <span class="text-lg">{button.description}</span>
                                </button>
                            {/each}
                        </div>
                    </Card.Content>
                </svelte:fragment>
            </WizardCard>
        {:else if wizardStage === "athlete"}
            <WizardCard>
                <svelte:fragment slot="content">
                    <Card.Content class="sm:p-6">
                        <Tabs.Root bind:value={athleteSetupTab}>
                            <Tabs.List class="grid grid-cols-4 mt-6 pointer-events-none mb-2">
                                <Tabs.Trigger value="info">Profile</Tabs.Trigger>
                                <Tabs.Trigger value="connections">Connections</Tabs.Trigger>
                                <Tabs.Trigger value="discover">Discover</Tabs.Trigger>
                                <Tabs.Trigger value="plan">Improve</Tabs.Trigger>
                            </Tabs.List>
                            <Card.Header class="text-center px-0 sm:px-6">
                                <Card.Title class="text-xl leading-none"> {athleteSetupInfo[athleteSetupTab].title} </Card.Title>
                                <Card.Description class="">{athleteSetupInfo[athleteSetupTab].description}</Card.Description>
                            </Card.Header>
                            <Separator class="mb-6 sm:mx-6 w-auto"/>
                            <Tabs.Content class="mt-0 sm:px-6 mx-auto" value="info">
                                <div class="flex justify-center">
                                    <form bind:this={profileInfoForm} on:submit|preventDefault>
                                        <div class="space-y-6">
                                            <div class="space-y-2 flex-1">
                                                <Label class="mb-0" for="wizard-birth-year">Year of birth</Label>
                                                <Input class="max-w-xs" bind:value={athleteBirthYear} id="wizard-birth-year" placeholder="e.g. 1987" type="number" min={1900} max={currentYear} required/>
                                            </div>
                                            <div class="space-y-2 flex-1">
                                                <Label class="mb-0">Gender</Label>
                                                <RadioGroup.Root bind:value={athleteGender} class="flex gap-4">
                                                    <div class="flex gap-2">
                                                        <RadioGroup.Item value="male" id="wizard-gender-male" tabindex={radioTabIndex(athleteGender, "male")} />
                                                        <Label class="mb-0 font-normal" for="wizard-gender-male">Male</Label>
                                                    </div>
                                                    <div class="flex gap-2">
                                                        <RadioGroup.Item value="female" id="wizard-gender-female" tabindex={radioTabIndex(athleteGender, "female")} />
                                                        <Label class="mb-0 font-normal" for="wizard-gender-female">Female</Label>
                                                    </div>
                                                    <div class="flex gap-2">
                                                        <RadioGroup.Item value="other" id="wizard-gender-other" tabindex={radioTabIndex(athleteGender, "other")} />
                                                        <Label class="mb-0 font-normal" for="wizard-gender-other">Other</Label>
                                                    </div>
                                                </RadioGroup.Root>
                                            </div>
                                            <div class="space-y-2 flex-1">
                                                <Label class="mb-0" for="wizard-weight">Weight</Label>
                                                <Input class="max-w-xs" bind:value={athleteWeight} id="wizard-weight" placeholder="Weight" type="number" step="any" required />
                                            </div>
                                            <div class="space-y-2 flex-1">
                                                <Label class="mb-0" for="wizard-weight">Unit</Label>
                                                <RadioGroup.Root bind:value={athleteWeightUnit} class="flex gap-4">
                                                    <div class="flex gap-2">
                                                        <RadioGroup.Item value="kg" id="wizard-weight-kg" />
                                                        <Label class="mb-0 font-normal" for="wizard-weight-kg">kg</Label>
                                                    </div>
                                                    <div class="flex gap-2">
                                                        <RadioGroup.Item value="lb" id="wizard-weight-lb" />
                                                        <Label class="mb-0 font-normal" for="wizard-weight-lb">lbs</Label>
                                                    </div>
                                                </RadioGroup.Root>
                                            </div>

                                            <!-- Optional: Country -->
                                            <div class="space-y-2 flex-1">
                                                <Label class="mb-0" for="wizard-country">Country <span class="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                                                <select id="wizard-country" bind:value={country} class="flex h-9 w-full max-w-xs rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring">
                                                    <option value="">Select country…</option>
                                                    {#each COUNTRIES as c}
                                                        <option value={c.code}>{c.name}</option>
                                                    {/each}
                                                </select>
                                            </div>

                                            <!-- Optional: Profile photo with crop -->
                                            <div class="space-y-2 flex-1">
                                                <Label class="mb-0">Profile Photo <span class="text-muted-foreground font-normal text-xs">(optional, max 5 MB)</span></Label>
                                                <AvatarCropper
                                                    onCrop={(blob) => { profilePhotoBlob = blob }}
                                                    onClear={() => { profilePhotoBlob = null }}
                                                />
                                                {#if photoError}
                                                    <p class="text-sm text-red-600">{photoError}</p>
                                                {/if}
                                            </div>

                                            <!-- Optional: Newsletter -->
                                            <div class="flex items-start gap-2">
                                                <Checkbox id="wizard-newsletter" bind:checked={newsletterOptIn} class="mt-0.5"/>
                                                <Label for="wizard-newsletter" class="mb-0 font-normal leading-snug cursor-pointer">
                                                    Keep me updated on new features, training tips, and offers
                                                </Label>
                                            </div>

                                            {#if !profileInfoValid}<div class="text-red-600 italic">*Please ensure all fields are filled out.</div>{/if}
                                        </div>
                                    </form>
                                </div>
                            </Tabs.Content>
                            <Tabs.Content class="mt-0 sm:px-6 text-base" value="connections">
                                    {#each Object.entries(providers) as [provider, info]}
                                        <div class="flex flex-col gap-1">
                                            <img class="w-40 self-start" src={athleteProvidersDetails[provider].logo} alt={provider}/>
                                            <div class="text-muted-foreground mt-1">{athleteProvidersDetails[provider].description}</div>
                                            <div class="space-x-1">
                                                {#if info.loggedIn} 
                                                    <i class="fa-regular text-green-600 fa-circle-check text-sm group-hover:no-underline"/>
                                                {:else}
                                                    <i class="fa-regular fa-link text-sm group-hover:no-underline"/>
                                                {/if}
                                                {#if provider === "garmin-health"}
                                                    <button type="button" class="group hover:underline text-left" on:click={() => showGarminHistoricalDialog = true}> {info.loggedIn ? "Connected" : "Connect"}</button>
                                                {:else}
                                                    <a class="group hover:underline text-left" on:click={saveSessionData} href={info.redirect}> {info.loggedIn ? "Connected" : "Connect"}</a>
                                                {/if}
                                            </div>
                                            {#if provider === "garmin-health"}
                                                <div class="text-sm text-amber-700">
                                                    To receive your past activities, you need to switch on <strong>Historical Data</strong> when you connect.
                                                    <button type="button" class="underline hover:no-underline" on:click={() => showGarminHistoricalDialog = true}>Show me how</button>
                                                </div>
                                            {/if}
                                        </div>
                                        <Separator class="mt-2 mb-4 last:hidden" />
                                    {/each}
                            </Tabs.Content>
                            <Tabs.Content class="mt-0 sm:px-6" value="discover">
                                <RadioGroup.Root bind:value={activityType} class="flex flex-col gap-4 flex-wrap">
                                    {#if !analyzeOptionSelected}<div class="text-red-600 italic">*Please select one of the following.</div>{/if}
                                    {#if garminUnavailable}
                                        <div class="border-2 border-amber-500 bg-amber-50 rounded-md px-4 py-3 text-base text-amber-900" role="alert">
                                            {garminUnavailableMessages[garminUnavailable]}
                                            Choose another way to set up for now.
                                            <button type="button" class="underline hover:no-underline" on:click={() => showGarminHistoricalDialog = true}>
                                                {garminUnavailable === "denied" ? "Reconnect Garmin" : "Reconnect Garmin and turn on Historical Data"}
                                            </button>
                                        </div>
                                    {:else if garminBusy}
                                        <div class="border-2 border-amber-500 bg-amber-50 rounded-md px-4 py-3 text-base text-amber-900" role="alert">
                                            Garmin is busy right now and couldn't take the request. Nothing is wrong with your account: wait a minute, then click Next to try again.
                                        </div>
                                    {/if}
                                    {#if providers["strava"].loggedIn}
                                        <Label class="flex flex-col relative border-x-2 border-b-2 border-t-2 rounded-b-md rounded-t-md px-6 py-3 mb-0 font-normal cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary" for="discover-strava">   
                                            <img class="w-40 self-start" src={athleteProvidersDetails["strava"].logo} alt={"strava"}/>
                                            <div class="text-muted-foreground mt-2 text-base">Choose this option to have Xert retrieve and analyze your activities from your Strava account.</div>
                                            {#if trial}<div class="italic mt-3 text-muted-foreground font-medium">*As part of the 30-Day Premium Trial, only activities within the last 3 months will be analyzed</div>{/if}
                                            <div class="text-green-600 bg-green-100 border-green-600 border rounded-full px-2 py-1 self-start mt-3">Recommended</div>
                                            <RadioGroup.Item class="absolute top-0 right-0 mt-2 mr-2" value="strava" id="discover-strava" tabindex={radioTabIndex(activityType, "strava")} />    
                                        </Label>
                                    {/if}
                                    {#if providers["garmin-health"].loggedIn}
                                        <Label class="flex flex-col relative border-x-2 border-b-2 border-t-2 rounded-b-md rounded-t-md px-6 py-3 mb-0 font-normal cursor-pointer hover:border-primary [&:has(button[data-state='checked'])]:border-primary {garminUnavailable ? 'opacity-50 grayscale cursor-not-allowed pointer-events-none' : ''}" for="discover-garmin" aria-disabled={garminUnavailable ? "true" : undefined}>   
                                            <img class="h-12 self-start" src={athleteProvidersDetails["garmin-health"].logo} alt={"garmin-health"}/>
                                            <div class="text-muted-foreground mt-2 text-base">Choose this option to have Xert retrieve and analyze your activities from your Garmin account. Garmin Connect will <strong>ONLY</strong> forward activities recorded on Garmin devices. Activities recorded on Zwift or manually uploaded will not be provided for analysis.</div>
                                            <div class="text-muted-foreground mt-2 text-base">Garmin sends your activities when it has them ready, which may take a few minutes. This needs <strong>Historical Data</strong> to be switched on for Xert in Garmin.</div>
                                            {#if trial}<div class="italic mt-3 text-muted-foreground font-medium">*As part of the 30-Day Premium Trial, only activities within the last 3 months will be analyzed</div>{/if}
                                            <RadioGroup.Item class="absolute top-0 right-0 mt-2 mr-2" value="garmin" id="discover-garmin" disabled={!!garminUnavailable} tabindex={radioTabIndex(activityType, "garmin")} />
                                        </Label>
                                    {/if}
                                    <div class="flex flex-col group">
                                        <Label 
                                            class="flex flex-col relative border-x-2 border-b-2 border-t-2 rounded-b-md rounded-t-md px-6 py-3 mb-0 font-normal cursor-pointer hover:border-primary 
                                                [&:has(button[data-state='checked'])]:border-primary [&:has(button[data-state='checked'])]:border-b-0 
                                                [&:has(button[data-state='checked'])]:rounded-b-none transition-[border-width,border-radius] delay-300 [&:has(button[data-state='checked'])]:delay-0 [&:has(button[data-state='checked'])]:duration-0
                                            " 
                                            for="discover-upload"
                                        >   
                                            <div class="leading-none font-medium text-lg">Upload Files</div>
                                            <div class="text-muted-foreground text-base mt-2">Drag-and-drop or bring your own TCX and FIT activity files.</div>
                                            {#if trial}<div class="italic mt-3 text-muted-foreground font-medium">*As part of the 30-Day Premium Trial, only activities within the last 3 months will be analyzed</div>{/if}
                                            <RadioGroup.Item class="absolute top-0 right-0 mt-2 mr-2" value="upload" id="discover-upload" tabindex={radioTabIndex(activityType, "upload")} />
                                        </Label>
                                        {#if activityType === "upload"}
                                            <div transition:slide>
                                                <div class="space-y-1 border-x-2 border-b-2 px-6 pb-3 group-[:has(button[data-state='checked'])]:border-primary rounded-b-md">
                                                    <Separator class="mb-4"/>
                                                    <Dropzone on:drop={handleFileSelect} accept={['.fit', '.tcx']}>
                                                        <div>
                                                            <div class="mb-2">Drag files here or click to select files</div>
                                                            <div>The maximum file size for uploads is <span class="font-bold">5 MB</span> for FIT files and <span class="font-bold">30 MB</span> for TCX files.</div>
                                                            <div>Only <span class="font-bold">FIT</span> and <span class="font-bold">TCX</span> files are supported.</div>
                                                        </div>
                                                    </Dropzone>
                                                    <div> Files Uploaded: {files.length}</div>
                                                </div>
                                            </div>
                                        {/if}
                                    </div>
                                    <div class="flex flex-col group">
                                        <Label 
                                            class="flex flex-col relative border-x-2 border-b-2 border-t-2 rounded-b-md rounded-t-md px-6 py-3 mb-0 font-normal cursor-pointer hover:border-primary 
                                                [&:has(button[data-state='checked'])]:border-primary [&:has(button[data-state='checked'])]:border-b-0 
                                                [&:has(button[data-state='checked'])]:rounded-b-none transition-[border-width,border-radius] delay-300 [&:has(button[data-state='checked'])]:delay-0 [&:has(button[data-state='checked'])]:duration-0
                                            " 
                                            for="discover-manual"
                                        >   
                                            <div class="leading-none font-medium text-lg">I don't have any data. Ask me questions instead.</div>
                                            <div class="text-muted-foreground text-base mt-2">Answer a series of questions to help Xert get your riding profile properly set up and ready for training when you don't have any data to sync or upload.</div>
                                            <RadioGroup.Item class="absolute top-0 right-0 mt-2 mr-2" value="manual" id="discover-manual" tabindex={radioTabIndex(activityType, "manual")} />
                                        </Label>
                                        {#if activityType === "manual"}
                                            {@const functionalWeight = getFunctionalWeight(athleteWeight, athleteWeightUnit)}
                                            {@const ftpDefault = round(functionalWeight*2.5, 1)}
                                            {@const ppDefault = round(functionalWeight*12, 1)}
                                            <div transition:slide>
                                                <form bind:this={manualInputForm} on:submit|preventDefault>
                                                    <div class="flex flex-col gap-8 border-x-2 border-b-2 px-6 pb-3 group-[:has(button[data-state='checked'])]:border-primary rounded-b-md">
                                                        <Separator class="-mb-4"/>

                                                        <div class="space-y-3">
                                                            <Label for="manual-ftp" class="space-y-2 mb-0 font-normal">
                                                                <div class="font-medium leading-none">{"Threshold Power (W)"}</div>
                                                                <div class="text-sm text-muted-foreground">Your Threshold Power (often called TP or FTP) is the highest power you are able to sustain for a very long time, typically one hour. If you're not sure what your TP is, Xert can look for patterns in your data when you have performed maximal efforts to estimate it for you.</div>
                                                            </Label>
                                                            <Input required class="max-w-[14rem]" step="any" name="ftp" id="manual-ftp" placeholder={ftpDefault.toString()} value={manualFtp ?? ftpDefault} on:input={(e) => manualFtp = e.currentTarget.value} type="number" min=0/>
                                                        </div>
                                                        
                                                        <div class="space-y-3">
                                                            <Label for="manual-pp" class="space-y-2 mb-0 font-normal">
                                                                <div class="font-medium leading-none">{"Peak Power (W)"}</div>
                                                                <div class="text-sm text-muted-foreground">Your Peak Power (often called PP or Pmax) is the maximum power you have ever reached for one second. You'll reach this number during sprint efforts where you are attempting to produce as much power as you can. If you don't know your Peak Power, look for opportunities on your rides where you can safely attempt your highest effort and Xert will adjust the value for you. Be sure to sustain the power for 5 seconds.</div>
                                                            </Label>
                                                            <Input required class="max-w-[14rem]" step="any" name="pp" id="manual-pp" placeholder={ppDefault.toString()} value={manualPp ?? ppDefault} on:input={(e) => manualPp = e.currentTarget.value} type="number" min=0/>
                                                        </div>
                                                                
                                                        <div class="space-y-3">
                                                            <Label for="manual-weekly-hours" class="space-y-2 mb-0 font-normal">
                                                                <div class="font-medium leading-none">Hours of Riding You Do Per Week</div>
                                                                <div class="text-sm text-muted-foreground">Review your recent riding history and estimate the average number of hours per week you've been riding in the past few months. Xert uses this number to help identify how much training should be recommended.</div>
                                                            </Label>
                                                            <div class="flex items-center gap-4 max-w-md">
                                                                <SelectSlider min={SUGGESTED_WEEKLY_HOURS.min} max={SUGGESTED_WEEKLY_HOURS.max} step={0.5} bind:value={manualWeeklyHours} name="weekly_hours" id="manual-weekly-hours"/>
                                                                <span class="font-semibold w-20 text-right shrink-0">{manualWeeklyHours}<span class="text-slate-500 text-sm font-normal"> h</span></span>
                                                            </div>
                                                        </div>

                                                        <div class="space-y-4">
                                                            <div class="space-y-2">
                                                                <div class="font-medium leading-none">Weekly Availabilities</div>
                                                                <div class="text-sm text-muted-foreground">Based on the hours of riding you do per week above, we suggest a week of rides, shorter on weekdays and longer at the weekend, spreading onto more days as the hours grow. You can adjust the availability you have on each day-of-the-week if you plan to generate a training plan. You can change these at any time, including adjusting individual days, once your account has been set up.</div>
                                                            </div>
                                                            <!-- Recreated when the weekly hours change: its popovers copy the suggested availability when made. -->
                                                            {#key manualWeeklyHours}
                                                                <DefaultAvailabilityPicker bind:defaultAvailabilities {trainingMedians}/>
                                                            {/key}
                                                        </div>

                                                        {#if !manualFormFilled}<div class="text-red-600 italic -mt-4">*Please ensure Hours of Riding You Do Per Week, Threshold Power and Peak Power are filled in and are positive.</div>{/if}
                                                    </div>
                                                </form>
                                            </div>
                                        {/if}
                                    </div>
                                </RadioGroup.Root>
                            </Tabs.Content>
                            <Tabs.Content class="mt-0 sm:px-6" value="plan">
                                {#if forecastData}
                                    <FirstTimePlanStep data={forecastData} {trial} onFinish={leaveWizard} onPrevious={athleteSetupBackButton}/>
                                {/if}
                            </Tabs.Content>
                        </Tabs.Root>
                    </Card.Content>
                    <!-- The plan step is last: its Previous, Skip and Generate buttons are on its own bottom row -->
                    {#if athleteSetupTab !== "plan"}
                        <Card.Footer class="justify-end gap-2">
                            {#if athleteSetupTab != "info"}<Button variant="outline" on:click={athleteSetupBackButton} disabled={disableFinish}>Back</Button>{/if}
                            <Button on:click={athleteSetupForwardButton} disabled={disableFinish}>Next</Button>
                        </Card.Footer>
                    {/if}
                </svelte:fragment>
            </WizardCard>
        {:else if wizardStage === "coach"}
            <WizardCard>
                <svelte:fragment slot="content">
                    <Card.Header class="items-center">
                        <Card.Title class="text-xl leading-none">Build Your Community</Card.Title>
                        <Card.Description>{userFirstName}, tells us about your community</Card.Description>
                    </Card.Header>
                    <Separator class="mb-6 sm:mx-6 w-auto"/>
                    <Card.Content>
                        <div class="space-y-4">
                            <div class="space-y-2">
                                <Label class="mb-0" for="wizard-community-name" >Community Name</Label>
                                <Input id="wizard-community-name" placeholder="Community Name"/>
                            </div>
                            <div class="space-y-2">
                                <Label class="mb-0" for="wizard-community-webiste">Website</Label>
                                <Input id="wizard-community-website" placeholder="Website"/>
                            </div>
                            <div class="space-y-2">
                                <Label class="mb-0" for="wizard-community-twitter">Twitter</Label>
                                <Input id="wizard-community-twitter" placeholder="Twitter"/>
                            </div>
                            <div class="space-y-2">
                                <Label class="mb-0" for="wizard-community-facebook">Facebook</Label>
                                <Input id="wizard-community-facebook" placeholder="Facebook"/>
                            </div>
                            <div class="space-y-2">
                                <Label class="mb-0" for="wizard-community-decsription">Description</Label>
                                <Textarea id="wizard-community-description" placeholder="Details about your community"/>
                            </div>
                        </div>      
                    </Card.Content>
                    <Card.Footer class="justify-end gap-2">
                        <Button variant="outline" on:click={athleteSetupBackButton}>Back</Button>
                        <Button on:click={athleteSetupForwardButton} disabled={disableFinish}>{athleteSetupTab === "discover" ? "Finish" : "Next"}</Button>
                    </Card.Footer>
                </svelte:fragment>
            </WizardCard>
        {/if}
    </div>
</div>


{#if processing === "garmin"}
    <CyclingProgress overlay percent={garminPercent} timeLeftMs={garminTimeLeft}
        title="Downloading your activities from Garmin"
        subtitle="Garmin sends them when it has them ready. This may take a few minutes."
        messages={garminLate
            ? ["Garmin is taking a little longer than usual…", "Still waiting for Garmin…"]
            : ["Asking Garmin for your rides…", "Waiting for Garmin to send your activities…", "Gathering the last 90 days of riding…"]}
    />
{:else if processing !== "idle"}
    <CyclingProgress overlay listen={['trickle', 'progress']} done={processing === "done"}
        title="Analyzing your riding history"
        messages={["Clipping in…", "Reading your rides…", "Finding your threshold power…", "Measuring your peak power…", "Estimating your high intensity energy…", "Building your fitness signature…"]}
        doneTitle="Your riding history is analyzed!"
        doneMessage="Next, let's plan your training."
    />
{/if}

<!-- Garmin: Historical Data has to be switched on by the user on Garmin's page -->
<Dialog.Root bind:open={showGarminHistoricalDialog} portal="#shadcn-ui-dialog-target">
    <Dialog.Content class="max-w-lg max-h-[95dvh] overflow-y-auto" overlayClass="bg-black/50 backdrop-blur-none">
        <Dialog.Header>
            <Dialog.Title>Switch on Historical Data</Dialog.Title>
            <Dialog.Description>
                Xert needs <strong>Historical Data</strong> to receive your past activities from Garmin and analyze your riding history.
                Garmin leaves it switched off, and only you can turn it on, so it is up to you to do it on Garmin's page.
            </Dialog.Description>
        </Dialog.Header>
        <div class="flex flex-col sm:flex-row gap-4 items-start">
            <ol class="list-decimal pl-5 space-y-2 text-base flex-1">
                <li>Continue to Garmin and sign in.</li>
                <li>Under <em>Data shared from Garmin to the Xert app</em>, switch on <strong>Historical Data</strong>.</li>
                <li>Click <strong>Save</strong>.</li>
            </ol>
            <div class="relative w-56 shrink-0 self-center sm:self-start border rounded-md overflow-hidden bg-white shadow-sm">
                <img src="/assets/images/providers/garmin_historical_data.png" alt="Garmin's page, with the Historical Data switch off" class="block w-full"/>
                <span class="absolute left-[19%] w-[47%] top-[50.2%] h-[6.6%] rounded border-2 border-amber-500 animate-pulse pointer-events-none"></span>
            </div>
        </div>
        <p class="text-sm text-muted-foreground">
            Without it Xert can't receive your past activities. You can still upload your activity files, or answer a few questions instead.
        </p>
        <Dialog.Footer class="mt-2 flex gap-2 sm:flex-row-reverse">
            <Button href={providers["garmin-health"].redirect} on:click={saveSessionData}>Continue to Garmin</Button>
            <Button variant="outline" on:click={() => showGarminHistoricalDialog = false}>Not now</Button>
        </Dialog.Footer>
    </Dialog.Content>
</Dialog.Root>

<!-- Newsletter opt-out confirmation dialog -->
<Dialog.Root bind:open={showNewsletterWarning} portal="#shadcn-ui-dialog-target">
    <Dialog.Content class="max-w-sm" overlayClass="bg-black/50 backdrop-blur-none">
            <Dialog.Header>
                <Dialog.Title>Are you sure?</Dialog.Title>
                <Dialog.Description>
                    You don't want to miss announcements of all the exciting innovations coming out of the Xert Breakthrough Lab.
                </Dialog.Description>
            </Dialog.Header>
            <Dialog.Footer class="mt-4 flex gap-2 sm:flex-row-reverse">
                <Button on:click={() => {
                    newsletterOptIn = true
                    showNewsletterWarning = false
                    newsletterWarningShown = true
                    advanceFromInfo()
                }}>
                    Yes, Keep me informed
                </Button>
                <Button variant="outline" on:click={() => {
                    showNewsletterWarning = false
                    newsletterWarningShown = true
                    advanceFromInfo()
                }}>
                    No
                </Button>
            </Dialog.Footer>
    </Dialog.Content>
</Dialog.Root>

<style lang="scss">

    .header-background {
        background: url("/assets/images/Hero-Carousel-4-bg.jpg");
    }

</style>